import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gameAPI } from '../../services/api';
import { useGameStore } from '../../store/gameStore';
import { POPULAR_UNIVERSES, LANGUAGES } from '../../types/game.types';
import i18n from '../../i18n/config';

export default function GameSetup() {
  const { t } = useTranslation();
  const [gameName, setGameName] = useState('');
  // No default — the universe is required and the player must choose it themselves.
  const [selectedUniverse, setSelectedUniverse] = useState('');
  const [customUniverse, setCustomUniverse] = useState('');
  // Default the game's language to the admin's current interface language.
  const [language, setLanguage] = useState(() => i18n.language || 'en');
  const [maxPlayers, setMaxPlayers] = useState(2);

  const { setCurrentGame, setLoading, setError, setSetupStep, addRecentGame, error } = useGameStore();
  // Local submit flag — NOT the global store `isLoading`. The global flag is shared across
  // unrelated flows, so a stale `true` from a previous/other operation used to leave every field
  // here `disabled` and un-typeable until it happened to reset. This keeps the form editable.
  const [submitting, setSubmitting] = useState(false);

  const handleCreateGame = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!gameName.trim()) {
      setError(t('setup.enterGameName'));
      return;
    }

    const universe = selectedUniverse === 'custom' ? customUniverse : selectedUniverse;

    if (!universe.trim()) {
      setError(t('setup.chooseUniverse'));
      return;
    }

    try {
      setSubmitting(true);
      setLoading(true);
      setError(null);

      const game = await gameAPI.createGame({
        name: gameName,
        universe,
        language,
        maxPlayers,
      });

      setCurrentGame(game);
      addRecentGame(game); // Save to localStorage
      setSetupStep('add-players');
    } catch (error: any) {
      setError(error.response?.data?.error || t('setup.createError'));
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto 6rem auto', animation: 'fadeIn 0.6s ease-out' }}>
      {/* Hero Section */}
      <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          {t('setup.title')}
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#a0aec0' }}>
          {t('setup.subtitle')}
        </p>
      </div>

      {/* Form Card */}
      <div style={{
        background: 'rgba(15, 12, 41, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        padding: '2.5rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      }}>
        <form onSubmit={handleCreateGame}>
          {/* Game Name */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: '600',
              fontSize: '1rem',
              color: '#e2e8f0',
            }}>
              📝 {t('game.name')}
            </label>
            <input
              type="text"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              placeholder={t('setup.gameNamePlaceholder')}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                fontSize: '1rem',
                border: '2px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                color: '#fff',
                transition: 'all 0.3s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                e.target.style.boxShadow = '0 0 20px rgba(99, 102, 241, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Language */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: '600',
              fontSize: '1rem',
              color: '#e2e8f0',
            }}>
              🌍 {t('setup.languageLabel')}
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: '0.75rem',
              maxHeight: '300px',
              overflowY: 'auto',
              padding: '0.5rem',
            }}>
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  onClick={() => setLanguage(lang.value)}
                  disabled={submitting}
                  style={{
                    padding: '0.75rem',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    border: language === lang.value ? '2px solid rgba(99, 102, 241, 0.8)' : '2px solid rgba(99, 102, 241, 0.3)',
                    borderRadius: '10px',
                    background: language === lang.value ? 'rgba(99, 102, 241, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (language !== lang.value) {
                      e.currentTarget.style.background = 'rgba(99, 102, 241, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (language !== lang.value) {
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                      e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                    }
                  }}
                >
                  {lang.flag} {lang.label}
                </button>
              ))}
            </div>
          </div>

          {/* Universe */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: '600',
              fontSize: '1rem',
              color: '#e2e8f0',
            }}>
              🌌 {t('game.universe')}
            </label>
            <select
              value={selectedUniverse}
              onChange={(e) => setSelectedUniverse(e.target.value)}
              disabled={submitting}
              style={{
                width: '100%',
                padding: '1rem 1.25rem',
                fontSize: '1rem',
                border: '2px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.3s',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.6)';
                e.target.style.boxShadow = '0 0 20px rgba(99, 102, 241, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.3)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="" disabled style={{ background: '#1a1a2e' }}>
                {t('setup.selectUniverse')}
              </option>
              {POPULAR_UNIVERSES.map((univ) => (
                <option key={univ.value} value={univ.value} style={{ background: '#1a1a2e' }}>
                  {language === 'ru' ? univ.nameRu : univ.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Universe Input */}
          {selectedUniverse === 'custom' && (
            <div style={{ marginBottom: '2rem', animation: 'fadeIn 0.3s ease-out' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.75rem',
                fontWeight: '600',
                fontSize: '1rem',
                color: '#e2e8f0',
              }}>
                ✨ {t('setup.describeWorldLabel')}
              </label>
              <textarea
                value={customUniverse}
                onChange={(e) => setCustomUniverse(e.target.value)}
                placeholder={t('setup.describeWorldPlaceholder')}
                rows={4}
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '1rem 1.25rem',
                  fontSize: '1rem',
                  border: '2px solid rgba(168, 85, 247, 0.4)',
                  borderRadius: '12px',
                  background: 'rgba(168, 85, 247, 0.1)',
                  color: '#fff',
                  transition: 'all 0.3s',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  lineHeight: '1.6',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(168, 85, 247, 0.6)';
                  e.target.style.boxShadow = '0 0 20px rgba(168, 85, 247, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(168, 85, 247, 0.4)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          )}

          {/* Max Players */}
          <div style={{ marginBottom: '2.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '1rem',
              fontWeight: '600',
              fontSize: '1rem',
              color: '#e2e8f0',
            }}>
              👥 {t('setup.maxPlayers')}: <span style={{ color: '#667eea', fontSize: '1.2rem' }}>{maxPlayers}</span>
            </label>
            <input
              type="range"
              min="1"
              max="10"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(parseInt(e.target.value))}
              disabled={submitting}
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: 'rgba(99, 102, 241, 0.2)',
                outline: 'none',
                WebkitAppearance: 'none',
              }}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '0.5rem',
              fontSize: '0.85rem',
              color: '#718096'
            }}>
              <span>1</span>
              <span>5</span>
              <span>10</span>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '1.25rem',
              padding: '0.9rem 1.1rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '12px',
              color: '#fca5a5',
              fontSize: '0.95rem',
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '1.25rem',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '12px',
              background: submitting
                ? 'linear-gradient(135deg, #555 0%, #666 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s',
              boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)',
            }}
            onMouseEnter={(e) => {
              if (!submitting) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(99, 102, 241, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(99, 102, 241, 0.3)';
            }}
          >
            {submitting ? `⏳ ${t('setup.creating')}` : `🚀 ${t('setup.submit')}`}
          </button>
        </form>

        {/* Info Footer */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          background: 'rgba(99, 102, 241, 0.05)',
          borderRadius: '12px',
          border: '1px solid rgba(99, 102, 241, 0.1)',
        }}>
          <p style={{ fontSize: '0.85rem', color: '#a0aec0', textAlign: 'center', margin: 0 }}>
            💡 {t('setup.infoFooter')}
          </p>
        </div>
      </div>
    </div>
  );
}
